import React from 'react';
import { zoom, isIdentity, IDENTITY } from '../../../lib/photoManualTransform';
import { transformString } from '../../../lib/photoRender';
import {
  useDragPan, OnPhotoControls, ZoomBadge, PanelBackdrop, GridOverlay,
  PhotoLabel, canonicalImgStyle, formatMetricLine,
} from './PanelBits';

// One pair as two adjacent panels. Renders a fragment of exactly two cells so
// the caller controls the grid — that is what lets two pairs share one 2x2.
export default function SideBySidePair({ pair, gridOn, labels, testIdSuffix = '' }) {
  const sides = [
    { key: 'before', side: pair.before, tone: 'dark', label: labels.before },
    { key: 'after', side: pair.after, tone: 'accent', label: labels.after },
  ];
  return (
    <>
      {sides.map(({ key, side, tone, label }) => (
        <Panel key={key} testId={`cmp-panel-${key}${testIdSuffix}`}
               side={side} tone={tone} label={label} gridOn={gridOn} labels={labels} />
      ))}
    </>
  );
}

function Panel({ side, tone, label, gridOn, testId, labels }) {
  const drag = useDragPan(side.setManual, true);
  if (!side.url) return <EmptySlot label={labels.missing} testId={testId} />;
  return (
    <div data-testid={testId} {...drag}
         className="relative aspect-[9/16] rounded-xl overflow-hidden bg-black ring-1 ring-white/10 shadow-xl">
      <PanelBackdrop url={side.url} />
      <img src={side.url} alt="" data-testid="cmp-img" draggable={false}
           style={canonicalImgStyle(side.canonical, transformString(null, side.manual))} />
      {gridOn && <GridOverlay />}
      <PhotoLabel position="top-left" tone={tone}
                  metric={formatMetricLine(side.weightKg, side.bodyFat)} label={label} />
      <ZoomBadge manual={side.manual} position="top-right" />
      <OnPhotoControls
        compact
        onZoomOut={() => side.setManual((m) => zoom(m, -1, side.canonical))}
        onZoomIn={() => side.setManual((m) => zoom(m, +1, side.canonical))}
        onAuto={side.autoMatch}
        autoTitle={labels.mismatch}
        onReset={isIdentity(side.manual) ? null : () => side.setManual(IDENTITY)}
      />
    </div>
  );
}

export function EmptySlot({ label, testId }) {
  return (
    <div data-testid={testId}
         className="relative aspect-[9/16] rounded-xl overflow-hidden bg-muted/30 ring-1 ring-white/10
                    flex items-center justify-center p-2">
      <p className="text-[10px] text-center text-muted-foreground uppercase tracking-wide">{label}</p>
    </div>
  );
}
